import React, {useState, useEffect, useRef} from 'react'
import {
  doc,
  collection,
  onSnapshot,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore'
import db from 'services/firebase'
import {useAuth} from 'app/modules/auth'
import Drawer from 'react-modern-drawer'
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  showLoadingToast,
  updateLoadingToast,
  dismissLoadingToast,
} from 'app/components/ShowToast'
import createDependabotService from 'app/Integrations/GithubDependabot'
import EnableDependabotModal from '../modals/EnableDependabotModal'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import {AddDrawerIntegrationProps, DependabotRepository} from '../utils/types'
import InfoCard from '../drawer-components/InfoCard'
import DrawerFooter from '../drawer-components/DrawerFooter'
import {
  addIntegrationTests,
  removeIntegrationTests,
} from 'app/Integrations/services/integrationTestsService'
import Button from 'app/components/Button'

const GithubDependabotDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const isConnected =
    integration.length > 0 &&
    integration[0]?.status === 'Connected' &&
    integration[0]?.name === 'Github Dependabot'
  const [isScanning, setIsScanning] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [showDependabotModal, setShowDependabotModal] = useState(false)
  const [disabledRepos, setDisabledRepos] = useState<string[]>([])
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [githubData, setGithubData] = useState<any[]>([])
  const [isGithubConnected, setIsGithubConnected] = useState(false)

  useEffect(() => {
    let unsubscribeGitHub: (() => void) | undefined
    let unsubscribeIntegration: (() => void) | undefined

    if (isOpen) {
      unsubscribeGitHub = getGitHubIntegration()
      if (id) {
        unsubscribeIntegration = getIntegration()
      }
    }

    return () => {
      if (unsubscribeGitHub) unsubscribeGitHub()
      if (unsubscribeIntegration) unsubscribeIntegration()
    }
  }, [id, isOpen])

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  const getGitHubIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const integrationsRef = collection(tenantRef, 'integrations')

      const unsubscribe = onSnapshot(integrationsRef, (snapshot) => {
        const integrationData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            name: doc.data().name,
            accessToken: doc.data().accessToken,
          }))
          .filter((integration) => integration.name === 'GitHub' || integration.name === 'Github')

        if (integrationData.length > 0) {
          setAccessToken(integrationData[0].accessToken)
          setGithubData(integrationData)
          setIsGithubConnected(true)
          localStorage.setItem('github_access_token', integrationData[0].accessToken)
        } else {
          setAccessToken(null)
          setGithubData([])
          setIsGithubConnected(false)
          localStorage.removeItem('github_access_token')
        }
      })

      return unsubscribe
    } catch (error) {
      console.error('Error getting GitHub integrations:', error)
    }
  }

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'Github Dependabot') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
            }
            setIntegration([integrationData])
            if (!isGithubConnected) {
              setAccessToken(data.accessToken)
            }
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  const handleConnect = async () => {
    if (!isGithubConnected) {
      showErrorToast({message: 'Please connect GitHub integration first'})
      return
    }
    if (accessToken) {
      setIsConnecting(true)
      let toastId
      try {
        toastId = showLoadingToast({message: 'Connecting GitHub Dependabot...'})

        if (!githubData[0]?.orgId) {
          dismissLoadingToast(toastId)
          showErrorToast({
            message: 'Organization ID not found. Please ensure your GitHub integration has proper organization access.'
          })
          setIsConnecting(false)
          return
        }

        localStorage.setItem('pendingIntegration', 'Github Dependabot')
        const currentDate = new Date()
        const formattedDate = `${currentDate.getFullYear()}-${String(
          currentDate.getMonth() + 1
        ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`

        const tenantRef = doc(db, 'tenants', currentUser?.tenantId || '')
        const githubRef = collection(tenantRef, 'integrations')
        const vendorsRef = collection(tenantRef, 'vendors')

        const newData = {
          accessToken: accessToken,
          name: 'Github Dependabot',
          group: 'Vulnerability Scanning & Security Assessment Platform',
          img_path: '/integrations/Github.svg',
          status: 'Connected',
          connectedAt: formattedDate,
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
          organizationId: githubData[0]?.orgId,
        }

        const integrationDocRef = await addDoc(githubRef, newData)
        const integrationId = integrationDocRef.id

        updateLoadingToast(toastId, {
          message: 'Setting up vendor information...',
          type: 'info',
          isLoading: true,
        })

        const vendorData = {
          authentication_type: '',
          category: 'Vulnerability Scanning & Security Assessment Platform',
          created_at: serverTimestamp(),
          due_deligence_status: 'Incomplete',
          impact_assessment: [],
          logo: [
            {
              name: 'github_dependabot_logo.png',
              size: 3395,
              url: '/integrations/Github.svg',
            },
          ],
          owner: currentUser?.userName || '',
          policy_url: 'https://github.com/security',
          reliance: '',
          risk_level: '',
          tenantId: currentUser?.tenantId,
          terms_url: 'https://github.com/terms',
          two_factor: true,
          uid: currentUser?.uid,
          updated_at: serverTimestamp(),
          vendor_name: 'Github Dependabot',
          website_url: 'https://github.com/features/security/software-supply-chain',
        }

        await addDoc(vendorsRef, vendorData)

        const activitiesRef = collection(tenantRef, 'activities')
        const currentDate2 = new Date()
        const todaysDate = `${
          currentDate2.getMonth() + 1
        }/${currentDate2.getDate()}/${currentDate2.getFullYear()}`
        await addDoc(activitiesRef, {
          message: `${currentUser?.userName} connected GitHub Dependabot integration`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          action: 'Integration connected',
          target: 'Integration Management',
          email: currentUser?.email,
          username: currentUser?.userName,
          date: todaysDate,
        })

        updateLoadingToast(toastId, {
          message: 'Scanning for vulnerabilities...',
          type: 'info',
          isLoading: true,
        })

        try {
          await handleScanVulnerabilitiesForConnect(integrationId)

          updateLoadingToast(toastId, {
            message: 'Adding integration tests...',
            type: 'info',
            isLoading: true,
          })

          try {
            await addIntegrationTests('Github Dependabot', currentUser?.tenantId || '', currentUser)
          } catch (testError) {
            console.error('Error adding GitHub Dependabot integration tests:', testError)
          }

          dismissLoadingToast(toastId)
          showSuccessToast({message: 'Successfully authenticated with GitHub Dependabot'})

          window.location.href = '/integrations'
          return
        } catch (scanError) {
          console.error('Error during automatic vulnerability scan:', scanError)
          dismissLoadingToast(toastId)
          showSuccessToast({
            message:
              'GitHub Dependabot connected successfully. You can manually scan for vulnerabilities later.',
          })
          window.location.href = '/integrations'
          return
        }
      } catch (error: any) {
        dismissLoadingToast(toastId)
        showErrorToast({message: 'GitHub Dependabot connection failed, please try again later'})
        console.error('Error connecting GitHub Dependabot:', error)
        throw error
      } finally {
        setIsConnecting(false)
      }
    } else {
      showErrorToast({
        message: 'No GitHub access token found. Please connect GitHub integration first.',
      })
    }
  }

  const handleDisconnect = async () => {
    try {
      setIsDisconnecting(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return

      const tenantRef = doc(db, 'tenants', tenantId)
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      await deleteDoc(integrationRef)
      const vendorsRef = collection(tenantRef, 'vendors')
      const githubVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Github Dependabot'))
      const githubVendorSnapshot = await getDocs(githubVendorQuery)

      const deleteVendorPromises = githubVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      const vulnerabilitiesRef = collection(tenantRef, 'vulnerabilities')
      const dependabotVulnQuery = query(
        vulnerabilitiesRef,
        where('source', '==', 'Github Dependabot')
      )
      const dependabotVulnSnapshot = await getDocs(dependabotVulnQuery)

      const deleteVulnPromises = dependabotVulnSnapshot.docs.map(async (vulnDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vulnerabilities', vulnDoc.id))
      })

      await Promise.all([...deleteVendorPromises, ...deleteVulnPromises])

      try {
        await removeIntegrationTests('Github Dependabot', tenantId, currentUser)
      } catch (testError) {
        console.error('Error removing GitHub Dependabot integration tests:', testError)
      }

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected GitHub Dependabot integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'GitHub Dependabot integration disconnected'})

      localStorage.removeItem('github_access_token')
      setIntegration([])
      handleClose()
    } catch (error) {
      showErrorToast({message: 'Failed to disconnect GitHub Dependabot'})
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleScanVulnerabilitiesForConnect = async (integrationId: string) => {
    if (!isGithubConnected) {
      throw new Error('Please connect GitHub integration first')
    }
    if (!accessToken) {
      throw new Error('No access token available')
    }

    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        throw new Error('No tenant ID found')
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const assetsRef = collection(tenantRef, 'assets')
      const githubAssetsQuery = query(assetsRef, where('platform', '==', 'GitHub'))
      const githubAssetsSnapshot = await getDocs(githubAssetsQuery)
      const repositories = githubAssetsSnapshot.docs.map((doc) => {
        const data = doc.data() as DependabotRepository
        return {
          owner: {login: data.owner?.login || ''},
          name: data.repo_name,
          html_url: data.repo_link,
        }
      })

      if (repositories.length === 0) {
        showInfoToast({message: 'No GitHub repositories found to scan'})
        return
      }

      const dependabotService = createDependabotService(accessToken)
      const response = await dependabotService.scanRepositoriesForVulnerabilities(
        repositories,
        tenantId,
        integrationId
      )

      const currentDate = new Date().toISOString()
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationId)
      await updateDoc(integrationRef, {
        last_scan: currentDate,
      })

    } catch (error) {
      throw error
    }
  }

  const handleScanVulnerabilities = async () => {
    if (!isGithubConnected) {
      showErrorToast({message: 'Please connect GitHub integration first'})
      return
    }
    if (!isConnected || !accessToken) {
      showErrorToast({message: 'Please connect to GitHub Dependabot'})
      return
    }
    setShowDependabotModal(false)
    setIsScanning(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        throw new Error('No tenant ID found')
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const assetsRef = collection(tenantRef, 'assets')
      const githubAssetsQuery = query(assetsRef, where('platform', '==', 'GitHub'))
      const githubAssetsSnapshot = await getDocs(githubAssetsQuery)
      const repositories = githubAssetsSnapshot.docs.map((doc) => {
        const data = doc.data() as DependabotRepository
        return {
          owner: {login: data.owner?.login || ''},
          name: data.repo_name,
          html_url: data.repo_link,
        }
      })

      if (repositories.length === 0) {
        showInfoToast({message: 'No GitHub repositories found to scan'})
        return
      }

      const dependabotService = createDependabotService(accessToken)
      const response = await dependabotService.scanRepositoriesForVulnerabilities(
        repositories,
        tenantId,
        id || ''
      )

      const currentDate = new Date().toISOString()
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id || '')
      await updateDoc(integrationRef, {
        last_scan: currentDate,
      })

      if (response.success) {
        showSuccessToast({
          message: `Vulnerability scan completed. Found ${response.totalVulnerabilities} vulnerabilities across ${repositories.length} repositories.`,
        })
      } else if (response.disabledRepos && response.disabledRepos.length > 0) {
        setDisabledRepos(response.disabledRepos)
        setShowDependabotModal(true)
      } else {
        throw new Error(response.error || 'Failed to scan vulnerabilities')
      }
    } catch (error) {
      showErrorToast({message: 'Failed to scan vulnerabilities'})
    } finally {
      setIsScanning(false)
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    setShowDependabotModal(false)
    setDisabledRepos([])
    onClose()
  }

  return (
    <>
      <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
        <DrawerHeader title='Github Dependabot' onClose={onClose} />

        <div
          className='scroll-y overflow-auto p-6 mb-10'
          tabIndex={-1}
          style={{
            height: 'calc(100vh - 120px)',
            paddingBottom: '80px',
          }}
        >
          <div className='form'>
            <DrawerContent
              title='Github Dependabot'
              description='Dependency Vulnerability Scanning'
              logo='/media/integrations/github.svg'
              integration={integration}
              openAccordion={openAccordion}
              setOpenAccordion={setOpenAccordion}
              openAccordion2={openAccordion2}
              setOpenAccordion2={setOpenAccordion2}
              permissionInfo='SecureSlate will be granted permissions to access GitHub repositories and view Dependabot alerts via OAuth 2.0, respecting user permissions.'
              accessTypeinfo='Requires an active GitHub integration with repository access and security scope to retrieve Dependabot alerts and metadata.'
            />
            <div className='mb-8 d-flex justify-content-between align-items-center'>
              <div className='fs-6 fw-bold text-bold'>
                {!isGithubConnected
                  ? 'Connect GitHub to Enable Dependabot Scanning'
                  : isConnected
                  ? 'GitHub Dependabot Connected to SecureSlate'
                  : 'Connect GitHub to Enable Dependabot Scanning'}
              </div>

              {isConnected && isGithubConnected && (
                <Button
                  btnType='default'
                  onClick={handleScanVulnerabilities}
                  disabled={isScanning || !accessToken || !isGithubConnected}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-refresh me-2'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4' />
                    <path d='M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4' />
                  </svg>
                  {isScanning ? (
                    <>
                      Refreshing...
                      <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                    </>
                  ) : (
                    'Refresh'
                  )}
                </Button>
              )}

              {isConnected && !isGithubConnected && (
                <button
                  className='btn btn-sm btn-outline fs-6'
                  disabled={true}
                  style={{opacity: 0.6, cursor: 'not-allowed'}}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M7 7l5 5l-5 5' />
                    <path d='M13 7l-5 5l5 5' />
                  </svg>
                  Manual Scan
                </button>
              )}
            </div>
            {!isConnected && (
              <InfoCard
                type='info'
                title={
                  !isGithubConnected
                    ? 'Connect GitHub Integration First'
                    : 'How to enable GitHub Dependabot scanning?'
                }
                description={
                  !isGithubConnected
                    ? 'You need to connect your GitHub integration first before you can use GitHub Dependabot scanning. Please go to the integrations page and connect GitHub.'
                    : "Ensure your GitHub integration is connected. Click the 'Scan Vulnerabilities' button to check your repositories for dependency vulnerabilities using Dependabot alerts."
                }
              />
            )}
          </div>
        </div>
        <DrawerFooter
          isConnected={isConnected}
          isLoading={isConnecting || isDisconnecting}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          customActions={!isConnected}
          isDisable={!isGithubConnected}
        />
      </Drawer>
      <EnableDependabotModal
        show={showDependabotModal}
        onHide={() => setShowDependabotModal(false)}
        onRetry={handleScanVulnerabilities}
        repositories={disabledRepos}
      />
    </>
  )
}

export default GithubDependabotDrawer
